import { Children, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import React from 'react';
import partition from 'lodash/partition';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import format from '../../../../utils/format';
import addAllTags from '../../../../utils/result-tags';
import getTagsByXid from '../../../../stores/profile/use-profile-tags-store';
import Icon from '../../../atoms/icon';
import Row from 'react-bootstrap/Row';
import InteractiveElement from '../../../atoms/interactive-element';
import styles from './header-quote-bar.module.scss';
import Tag from '../../../molecules/screener/tag';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import ProfileAppContext from '../../../../stores/profile/profile-app-context';
import useProfileCommonStore from '../../../../stores/profile/use-profile-common-store';
import { CHANNELS } from '../../../../shared/screener-constants';

const className = augmentClassName(styles);

// eslint-disable-next-line prefer-arrow-callback
const HeaderQuoteBar = React.memo(function HeaderQuoteBar({ children }) {
  const [showQuoteBar, _setShowQuoteBar] = useState(true);
  const [myTiaaScrollContainer, setMyTiaaScrollContainer] = useState(null);
  const childrenArr = Children.toArray(children);
  const { commonState = {}, apiConfigBase = {} } = useProfileCommonStore();
  const { urlDomain, urlPrefix, yyy } = useContext(ProfileAppContext);
  const [expandTags, setExpandTags] = useState(false);
  const [item, setItem] = useState([{ collapseTags: [], tags: [] }]);
  const tagsContainerRef = useRef();
  //When event listeners are initialized on the inital render, they will always read from that renders state regardless of any updates that may happen
  //To get around this, we use a ref and update that everytime the state we want to track changes, then use that ref in the event listener
  const quoteBarStateRef = useRef(showQuoteBar);
  const setShowQuoteBar = data => {
    quoteBarStateRef.current = data;
    _setShowQuoteBar(data);
  };

  const addBoundariesStyle = () => {
    if (tagsContainerRef.current && tagsContainerRef.current.children.length) {
      let previousOffset = 0;
      for (const tagsElement of tagsContainerRef.current.children) {
        if (tagsElement.firstChild.offsetTop > previousOffset) {
          previousOffset = tagsElement.firstChild.offsetTop;
          tagsElement.firstChild.classList.add(className('move-right').className);
        }
      }
    }
  };

  const tiaaNavHeader = document.querySelector('header.navbar');
  const tiaaNavTriage = document.querySelector('nav.triage');
  const headerClosePoint =
    600 +
    (tiaaNavHeader ? tiaaNavHeader.clientHeight : 0) +
    (tiaaNavTriage ? tiaaNavTriage.clientHeight : 0);
  const headerOpenPoint = headerClosePoint - 300;

  if (!tiaaNavTriage && tiaaNavHeader) {
    tiaaNavHeader.style.cssText = 'position: fixed; right: 0; left:0; top:0; z-index: 1030;';
  }

  useEffect(() => {
    getTagsByXid(yyy, commonState.channel, apiConfigBase.xid)
      .then(response => {
        if (!(response.items && response.items.length)) {
          return;
        }
        setItem(addAllTags(response.items));
      })
      .catch(error => {
        console.error(error);
      });
  }, [apiConfigBase.xid, commonState.channel, yyy]);
  const hasTags = !!(item[0].collapseTags.length || item[0].tags.length);
  const hasMoreThanSixTags = hasTags && item[0].tags.length > 6;
  const tags = expandTags ? item[0].tags : item[0].tags.slice(0, 6);

  useEffect(() => {
    addBoundariesStyle();
  }, [tags]);

  useEffect(() => {
    const scrollContainer = document.querySelector('div.scroller');

    if (scrollContainer) {
      setMyTiaaScrollContainer(scrollContainer);
    }

    //Need to add testing for this
    const handleQuoteBarDisplay = () => {
      const quoteBarDisplayPoint = quoteBarStateRef.current ? headerClosePoint : headerOpenPoint;

      const hasScrolled = myTiaaScrollContainer
        ? myTiaaScrollContainer.scrollTop <= quoteBarDisplayPoint
        : document.documentElement.scrollTop <= quoteBarDisplayPoint;
      setShowQuoteBar(hasScrolled);
    };

    if (myTiaaScrollContainer) {
      myTiaaScrollContainer.addEventListener('scroll', handleQuoteBarDisplay);

      return function cleanupListener() {
        myTiaaScrollContainer.removeEventListener('scroll', handleQuoteBarDisplay);
      };
    }

    window.addEventListener('scroll', handleQuoteBarDisplay);

    return function cleanupListener() {
      window.removeEventListener('scroll', handleQuoteBarDisplay);
    };
  }, [headerClosePoint, headerOpenPoint, myTiaaScrollContainer]);

  // separate cells that have a responsive prop from those that don't
  const [responsive, nonResponsive] = partition(childrenArr, c => c.props.responsive !== undefined);

  return (
    showQuoteBar && (
      <>
        {hasTags && (
          <Container {...className('ml-0', 'pb-4', 'd-print-none')}>
            <Row>
              <Col lg="8" md="7" {...className('d-flex', 'flex-wrap')} ref={tagsContainerRef}>
                {tags.map((tag, index) => (
                  <div data-key={`${tag.tag || ''}-${index}`} key={`${tag.tag || ''}-${index}`}>
                    <Tag
                      label={tag.tag}
                      popoverMsg={tag.hoverText}
                      index={tags.findIndex(i => i.tag === tag.tag)}
                      isProfilePage
                      isMicrosites={
                        commonState.channel.toLowerCase() === CHANNELS.DYNAMICMICROSITES
                      }
                      isMyTiaa={commonState.channel.toLowerCase() === CHANNELS.MYTIAA}
                      urlDomain={urlDomain}
                      urlPrefix={urlPrefix}
                    />
                  </div>
                ))}
                {hasMoreThanSixTags && (
                  <InteractiveElement
                    {...className('profile-expand-tags-btn')}
                    onClick={() => setExpandTags(!expandTags)}
                    tabIndex={0}
                    role="button"
                  >
                    {expandTags ? '- Show less' : '+ Show more'}
                  </InteractiveElement>
                )}
              </Col>
            </Row>
          </Container>
        )}
        <div {...className('quote-bar')}>
          <div {...className('d-flex')}>{nonResponsive}</div>
          <div {...className('quote-bar__responsive-container', 'd-print-flex')}>{responsive}</div>
        </div>
      </>
    )
  );
});

HeaderQuoteBar.propTypes = {
  children: PropTypes.node
};

const QuoteBarCell = ({
  action,
  children,
  dateAsOf,
  dateAsOfPrefix,
  dateOptions,
  label,
  note,
  responsive,
  showAsOf,
  colorClass,
  ...props
}) => {
  const isResponsive = responsive === true || typeof responsive === 'number';
  const classesIfResponsive = (...classes) => {
    return isResponsive ? classes : [];
  };

  return (
    <dl
      {...className(
        'quote-bar__cell',
        'mb-0',
        ...classesIfResponsive(
          'd-md-flex',
          'd-lg-block',
          'order-lg-0',
          `order-${typeof responsive === 'number' ? responsive : 12}`
        )
      )}
      {...props}
    >
      <dt
        {...className(
          'quote-bar__label',
          'font-family-sans-serif',
          'font-weight-normal',
          'text-left',
          ...classesIfResponsive('d-lg-block')
        )}
      >
        {label}
        {isResponsive && showAsOf ? (
          <span
            {...className(
              'quote-bar__note',
              'ml-2',
              'font-family-sans-serif',
              'font-italic',
              'text-lowercase',
              'text-muted-inverse',
              'd-inline',
              'd-lg-none'
            )}
          >
            ({dateAsOfPrefix} <time>{format.date(dateAsOf, dateOptions)}</time>)
          </span>
        ) : null}
        {isResponsive && note ? (
          <span {...className('d-block', 'd-lg-none', 'quote-bar__note')}>{note}</span>
        ) : null}
      </dt>
      <dd
        {...className('quote-bar__value', 'mb-0', colorClass, ...classesIfResponsive('d-lg-block'))}
      >
        {children}
        {note && <span {...className('d-none', 'd-lg-block', 'quote-bar__note')}>{note}</span>}
        {showAsOf ? (
          <span
            {...className(
              'quote-bar__note',
              'mt-3',
              'font-family-sans-serif',
              'font-italic',
              'text-light',
              'd-block',
              'quote-bar__note--as-of',
              ...classesIfResponsive('d-sm-none', 'd-lg-block')
            )}
          >
            {dateAsOfPrefix} <time>{format.date(dateAsOf, dateOptions)}</time>
          </span>
        ) : null}
        {action && (
          <span {...className('d-none', 'd-lg-block', 'mt-3', 'font-size-10')}>{action}</span>
        )}
      </dd>
    </dl>
  );
};

QuoteBarCell.propTypes = {
  action: PropTypes.node,
  children: PropTypes.node,
  colorClass: PropTypes.string,
  dateAsOf: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dateAsOfPrefix: PropTypes.string,
  dateOptions: PropTypes.object,
  label: PropTypes.string.isRequired,
  note: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  responsive: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  showAsOf: PropTypes.bool
};

QuoteBarCell.defaultProps = {
  dateAsOfPrefix: 'As of'
};

const HeaderQuoteBarCell = ({ children, xs, md, ...props }) => {
  return (
    <dl {...className('quote-bar__cell')} {...props}>
      {children}
    </dl>
  );
};

HeaderQuoteBarCell.propTypes = {
  children: PropTypes.node,
  md: PropTypes.any,
  xs: PropTypes.any
};

// Unit value
const UnitValueCell = ({ asOfDate, value, ...props }) => {
  return (
    <QuoteBarCell
      dateAsOfPrefix="As of close"
      dateAsOf={asOfDate} // TODO: rename dateAsOf so that these can just flow through
      showAsOf={true}
      label="Unit Value"
      {...props}
    >
      {format.price(value, { formatString: '$0,0.0000' })}
    </QuoteBarCell>
  );
};

UnitValueCell.propTypes = {
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  value: PropTypes.number
};

const getColorClass = val => {
  if (val > 0) {
    return 'text-success-secondary';
  }
  if (val < 0) {
    return 'text-danger';
  }
  return 'text-neutral';
};

// Day Change
const DayChangeCell = ({ changePercent, value, formatOverride = '$0,0.0000', ...props }) => {
  return (
    <HeaderQuoteBarCell {...props}>
      <dt
        {...className(
          'quote-bar__label',
          'font-family-sans-serif',
          'font-weight-normal',
          'text-light'
        )}
      >
        Day Change
      </dt>
      <dd {...className('quote-bar__value', 'text-nowrap', getColorClass(value))}>
        {format.price(value, { formatString: formatOverride })}
        <span {...className('quote-bar__change-percent', 'ml-4')}>
          (
          {format.isValid(changePercent) && changePercent !== 0 && (
            <Icon.ChangeArrow
              {...className(
                'mr-3',
                (changePercent < 0 && 'flip') || (changePercent === 0 && 'd-none')
              )}
              role="img"
              aria-label={changePercent < 0 ? 'Price moves up' : 'Price moves down'}
            />
          )}
          {format.percent(changePercent, { formatString: '0,0.00' })})
        </span>
      </dd>
    </HeaderQuoteBarCell>
  );
};

DayChangeCell.propTypes = {
  changePercent: PropTypes.number,
  formatOverride: PropTypes.string,
  value: PropTypes.number
};

const YtdReturnCell = ({ asOfDate, value, isCrefR4, ...props }) => {
  return (
    // <HeaderQuoteBarCell {...props}>
    <QuoteBarCell
      dateAsOf={asOfDate}
      showAsOf
      label="YTD Return"
      colorClass={getColorClass(value)}
      {...props}
    >
      {format.percent(value)}
    </QuoteBarCell>
  );
};

YtdReturnCell.propTypes = {
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  value: PropTypes.number,
  isCrefR4: PropTypes.bool
};

const EstimatedExpenseCharge = ({ value, ...props }) => {
  return (
    <QuoteBarCell
      label="Estimated Expense Charge"
      action={<ButtonDisclosures color={'text-white'} module={'page-header'} />}
      {...props}
    >
      {format.percent(value)}
    </QuoteBarCell>
  );
};

EstimatedExpenseCharge.propTypes = {
  value: PropTypes.number
};

const MorningstarRating = ({
  asOfDate,
  category,
  fundsInCategory,
  responsive,
  value,
  ...props
}) => {
  const isResponsive = responsive === false || typeof responsive === 'number';
  const hasRatings = typeof value === 'number';

  const note = (
    <>
      Out of {format.number(fundsInCategory, { formatString: '0,0' })} funds in Category:
      <br {...(isResponsive ? className('d-none', 'd-lg-inline') : {})} />
      {format.text(category)}
    </>
  );

  return (
    <QuoteBarCell
      dateAsOf={asOfDate}
      label="Overall Morningstar Rating"
      note={hasRatings ? note : null}
      responsive={responsive}
      showAsOf={true}
      {...props}
    >
      {hasRatings ? (
        <>
          <span {...className('sr-only')}>
            {format.number(value, { formatString: '0' })} out of 5 stars
          </span>
          <span aria-hidden="true" {...className('quote-bar__graphic--active', 'text-nowrap')}>
            {[1, 2, 3, 4, 5].map(pos => (
              <Icon.Star
                key={pos}
                role="img"
                aria-label="Star"
                {...className(pos > value && 'text-light', pos > value && 'opacity-50', 'mr-2')}
              />
            ))}
          </span>
        </>
      ) : (
        <span {...className('quote-bar__value')}>N/A</span>
      )}
    </QuoteBarCell>
  );
};

MorningstarRating.propTypes = {
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  category: PropTypes.string,
  fundsInCategory: PropTypes.number,
  responsive: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  value: PropTypes.oneOf([1, 2, 3, 4, 5])
};

const AssetClass = ({ children, ...props }) => {
  return (
    <QuoteBarCell label="Asset Class" {...props}>
      {children}
    </QuoteBarCell>
  );
};

AssetClass.propTypes = {
  children: PropTypes.node
};

AssetClass.defaultProps = {
  responsive: undefined
};

const CurrentRate = ({ asOfDate, value, ...props }) => {
  return (
    <QuoteBarCell
      label="Current Rate"
      dateAsOf={asOfDate}
      showAsOf={true}
      dateAsOfPrefix="As of"
      {...props}
    >
      {format.percent(value)}
    </QuoteBarCell>
  );
};

CurrentRate.propTypes = {
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  value: PropTypes.number
};

const CurrentInterestRate = ({ value, ...props }) => {
  return (
    <QuoteBarCell label="Current Interest Rate" {...props}>
      {format.percent(value, { formatString: '0.000' })}
    </QuoteBarCell>
  );
};

CurrentInterestRate.propTypes = {
  value: PropTypes.number
};

const AnnualPercentageYield = ({ value, ...props }) => {
  return (
    <QuoteBarCell label="Annual Percentage Yield" {...props}>
      {format.percent(value)}
    </QuoteBarCell>
  );
};

AnnualPercentageYield.propTypes = {
  value: PropTypes.number
};

const AccountInceptionDate = ({ asOfDate, isOther, ...props }) => {
  const { commonState = {} } = useProfileCommonStore();
  return (
    <QuoteBarCell label={`${isOther ? '' : 'Account '}Inception Date`} {...props}>
      <Icon.Calendar {...className('d-print-none')} role="img" aria-label="Inception Date" />
      <span {...className('ml-3')}>{format.date(asOfDate, { formatString: 'MM/DD/YYYY' })}</span>
    </QuoteBarCell>
  );
};

AccountInceptionDate.propTypes = {
  asOfDate: PropTypes.string,
  isOther: PropTypes.bool
};

const GuaranteedRate = ({ value, ...props }) => {
  return (
    <QuoteBarCell label="Guaranteed Rate" {...props}>
      {format.percent(value)}
    </QuoteBarCell>
  );
};

GuaranteedRate.propTypes = {
  value: PropTypes.number
};

const DeclaredCreditingRate = ({ value, asOfDate, ...props }) => {
  const dateOptions = {
    formatString: 'MMM YYYY'
  };
  return (
    <QuoteBarCell
      label="Declared Crediting Rate"
      dateAsOf={asOfDate}
      showAsOf={true}
      dateAsOfPrefix="As of"
      dateOptions={dateOptions}
      {...props}
    >
      {format.percent(value)}
    </QuoteBarCell>
  );
};

DeclaredCreditingRate.propTypes = {
  asOfDate: PropTypes.string,
  value: PropTypes.number
};

const NetCreditingRate = ({ value, ...props }) => {
  return (
    <QuoteBarCell label="Net Crediting Rate" {...props}>
      {format.percent(value)}
    </QuoteBarCell>
  );
};

NetCreditingRate.propTypes = {
  value: PropTypes.number
};

const TotalContractFee = ({ value, ...props }) => {
  return (
    <QuoteBarCell label="Total Contract Fee" {...props}>
      {format.percent(value)}
    </QuoteBarCell>
  );
};

TotalContractFee.propTypes = {
  value: PropTypes.number
};

const formatExpenseRatio = ({ grossValue, netValue, isCITFAFund }) => {
  //TIAA-12416 CIT Fixed Annuities will not have gross/net data, so we display N/A instead of --

  if (isCITFAFund) return <span {...className('quote-bar__value')}>N/A</span>;

  return `${format.number(grossValue, { formatString: '0,0.00[0]' })}% / ${format.number(netValue, {
    formatString: '0,0.00[0]'
  })}%`;
};

const GrossNetExpenseRatio = ({
  grossValue,
  isCITFAFund,
  isCIT,
  isRetirePlus,
  netValue,
  ...props
}) => {
  return (
    <QuoteBarCell label="Gross / Net Expense Ratio" {...props}>
      {/*TIAA-12416 CIT Fixed Annuities will not have gross/net data, so we display N/A instead of --*/}
      {isCIT ? (
        formatExpenseRatio({ grossValue, netValue, isCITFAFund })
      ) : (
        <span {...className('text-nowrap')}>
          {isRetirePlus
            ? `${format.number(grossValue, { formatString: '0,0.00[000]' })}% / ${format.number(
                netValue,
                { formatString: '0,0.00[000]' }
              )}%`
            : `${format.percent(grossValue)} / ${format.percent(netValue)}`}
        </span>
      )}
    </QuoteBarCell>
  );
};

GrossNetExpenseRatio.defaultProps = {
  isCIT: false,
  isCITFAFund: false,
  isRetirePlus: false
};

GrossNetExpenseRatio.propTypes = {
  grossValue: PropTypes.number,
  isCIT: PropTypes.bool,
  isCITFAFund: PropTypes.bool,
  isRetirePlus: PropTypes.bool,
  netValue: PropTypes.number
};

const NetAndTotalExpenseRatio = ({ netRatio, totalRatio, ...props }) => {
  return (
    <QuoteBarCell label="Net Expense / Total Expense Ratio" {...props}>
      {`${format.percent(netRatio)} / ${format.percent(totalRatio)}`}
    </QuoteBarCell>
  );
};

NetAndTotalExpenseRatio.propTypes = {
  netRatio: PropTypes.number,
  totalRatio: PropTypes.number
};

const DailyNav = ({ value, asOfDate, formatOverride = '$0,0.0000', ...props }) => {
  return (
    <QuoteBarCell
      label="Daily NAV"
      showAsOf={true}
      dateAsOf={asOfDate}
      dateAsOfPrefix="As of close"
      {...props}
    >
      {format.price(value, { formatString: formatOverride })}
    </QuoteBarCell>
  );
};

DailyNav.propTypes = {
  value: PropTypes.number,
  formatOverride: PropTypes.string,
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
};

const SevenDayCurrentEffectiveYield = ({ currentYield, effectiveYield, asOfDate, ...props }) => {
  return (
    <QuoteBarCell
      label="7-Day Current/ 7-Day Effective Yield"
      showAsOf={true}
      dateAsOf={asOfDate}
      dateAsOfPrefix="As of"
      {...props}
    >
      {`${format.percent(currentYield)} / ${format.percent(effectiveYield)}`}
    </QuoteBarCell>
  );
};

SevenDayCurrentEffectiveYield.propTypes = {
  currentYield: PropTypes.number,
  effectiveYield: PropTypes.number,
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
};

const SevenDayYield = ({ value, asOfDate, ...props }) => {
  return (
    <QuoteBarCell
      label="7 Day Yield"
      showAsOf={true}
      dateAsOf={asOfDate}
      dateAsOfPrefix="As of"
      {...props}
    >
      {format.percent(value)}
    </QuoteBarCell>
  );
};

SevenDayYield.propTypes = {
  value: PropTypes.number,
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
};

const SecYield = ({ subValue, unsubValue, asOfDate, ...props }) => {
  return (
    <QuoteBarCell
      label="30 Day SEC Yield (Sub/Unsub)"
      showAsOf={true}
      dateAsOf={asOfDate}
      dateAsOfPrefix="As of"
      {...props}
    >
      <div {...className('text-nowrap')}>
        {`${format.percent(subValue)} / ${format.percent(unsubValue)}`}
      </div>
    </QuoteBarCell>
  );
};

SecYield.propTypes = {
  subValue: PropTypes.number,
  unsubValue: PropTypes.number,
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
};

const PortfolioNetAssets = ({ netAssets, asOfDate, ...props }) => {
  return (
    <QuoteBarCell
      label="Portfolio Net Assets"
      showAsOf
      dateAsOf={asOfDate}
      dateAsOfPrefix="As of"
      {...props}
    >
      {`${format.price(netAssets, { formatString: '0.00 a' })}`}
    </QuoteBarCell>
  );
};

PortfolioNetAssets.defaultProps = {
  asOfDate: ''
};

PortfolioNetAssets.propTypes = {
  netAssets: PropTypes.number,
  asOfDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
};

const ContractValues = ({ value, asOfDate, ...props }) => {
  return (
    <QuoteBarCell label="Product Contract Value" dateAsOf={asOfDate} showAsOf {...props}>
      {format.price(value)}
      {value ? 'M' : ''}
    </QuoteBarCell>
  );
};

ContractValues.propTypes = {
  asOfDate: PropTypes.string,
  value: PropTypes.number
};

HeaderQuoteBar.AccountInceptionDate = AccountInceptionDate;
HeaderQuoteBar.AnnualPercentageYield = AnnualPercentageYield;
HeaderQuoteBar.AssetClass = AssetClass;
HeaderQuoteBar.ContractValues = ContractValues;
HeaderQuoteBar.CurrentInterestRate = CurrentInterestRate;
HeaderQuoteBar.CurrentRate = CurrentRate;
HeaderQuoteBar.DailyNav = DailyNav;
HeaderQuoteBar.DayChange = DayChangeCell;
HeaderQuoteBar.DeclaredCreditingRate = DeclaredCreditingRate;
HeaderQuoteBar.EstimatedExpenseCharge = EstimatedExpenseCharge;
HeaderQuoteBar.GrossNetExpenseRatio = GrossNetExpenseRatio;
HeaderQuoteBar.GuaranteedRate = GuaranteedRate;
HeaderQuoteBar.MorningstarRating = MorningstarRating;
HeaderQuoteBar.NetAndTotalExpenseRatio = NetAndTotalExpenseRatio;
HeaderQuoteBar.NetCreditingRate = NetCreditingRate;
HeaderQuoteBar.PortfolioNetAssets = PortfolioNetAssets;
HeaderQuoteBar.SecYield = SecYield;
HeaderQuoteBar.SevenDayCurrentEffectiveYield = SevenDayCurrentEffectiveYield;
HeaderQuoteBar.SevenDayYield = SevenDayYield;
HeaderQuoteBar.TotalContractFee = TotalContractFee;
HeaderQuoteBar.UnitValue = UnitValueCell;
HeaderQuoteBar.YtdReturn = YtdReturnCell;

export default HeaderQuoteBar;
